// Shared optical value option sets + a reusable dropdown used across the
// eye-test / prescription forms so staff can pick values instead of typing.

const fmtSigned = (n) => {
  const r = Math.round(n * 100) / 100;
  if (r === 0) return "0.00";
  return r > 0 ? `+${r.toFixed(2)}` : r.toFixed(2);
};

const range = (start, end, step) => {
  const out = [];
  // build descending (positive → negative) for power charts
  for (let v = start; v >= end - 1e-9; v -= step) out.push(Math.round(v * 100) / 100);
  return out;
};

// SPH: +30.00 → -30.00 (step 0.25)
export const SPH_OPTIONS = range(30, -30, 0.25).map(fmtSigned);

// CYL: +7.00 → -7.00 (step 0.25), both (+) and (-) values
export const CYL_OPTIONS = range(7, -7, 0.25).map(fmtSigned);

// Axis: 5 → 180 (step 5)
export const AXIS_OPTIONS = Array.from({ length: 36 }, (_, i) => String((i + 1) * 5));

// Vision / VA (Snellen): 6/60 … 6/6
export const VA_OPTIONS = ["6/60", "6/36", "6/24", "6/18", "6/12", "6/9", "6/6"];

// Add power: +1.00 → +5.00 (step 0.25) — only (+) values
export const ADD_OPTIONS = (() => {
  const out = [];
  for (let v = 1; v <= 5 + 1e-9; v += 0.25) out.push(`+${(Math.round(v * 100) / 100).toFixed(2)}`);
  return out;
})();

export const OPTION_MAP = {
  sph: SPH_OPTIONS,
  cyl: CYL_OPTIONS,
  axis: AXIS_OPTIONS,
  vn: VA_OPTIONS,
  va: VA_OPTIONS,
  add: ADD_OPTIONS,
};

/**
 * OpticalSelect — a styled native <select>. Native picker works great on touch
 * (opens the full value chart). Preserves an out-of-list saved value so the
 * edit form never loses existing data.
 */
/**
 * OpticalCombo — creatable dropdown (text input + <datalist>). User can pick
 * from suggestions OR type their own value. Perfect for Vision fields.
 */
export function OpticalCombo({ name, value, onChange, options, placeholder = "", className = "", testId }) {
  const listId = `dl-${name}`;
  return (
    <>
      <input
        list={listId}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
        className={
          className ||
          "w-full h-10 mt-1 rounded-md border border-slate-300 bg-white px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        }
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );
}

export function OpticalSelect({ name, value, onChange, options, placeholder = "Select…", className = "", testId }) {
  const val = value || "";
  const hasVal = val !== "" && !options.includes(val);
  return (
    <select
      name={name}
      value={val}
      onChange={onChange}
      data-testid={testId}
      className={
        className ||
        "w-full h-10 mt-1 rounded-md border border-slate-300 bg-white px-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      }
    >
      <option value="">{placeholder}</option>
      {hasVal && <option value={val}>{val}</option>}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
